import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { HomeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Helmet } from 'react-helmet-async'

const NotFound = () => {
    return (
        <>
            <Helmet>
                <title>404 - Page Not Found | Margdarshak</title>
                <meta name="description" content="The page you are looking for could not be found." />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center"
                    >
                        {/* 404 Illustration */}
                        <div className="mb-8">
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-9xl font-bold text-primary-600 mb-4"
                            >
                                404
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                className="w-24 h-1 bg-gradient-to-r from-primary-600 to-primary-700 mx-auto rounded-full"
                            />
                        </div>

                        {/* Error Message */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.6 }}
                        >
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">
                                Oops! Page Not Found
                            </h1>
                            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                                The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
                            </p>
                        </motion.div>

                        {/* Action Buttons */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.8 }}
                            className="flex flex-col sm:flex-row gap-4 justify-center"
                        >
                            <Link
                                to="/"
                                className="btn-primary btn-lg inline-flex items-center space-x-2"
                            >
                                <HomeIcon className="w-5 h-5" />
                                <span>Go Home</span>
                            </Link>

                            <button
                                onClick={() => window.history.back()}
                                className="btn-outline btn-lg inline-flex items-center space-x-2"
                            >
                                <ArrowLeftIcon className="w-5 h-5" />
                                <span>Go Back</span>
                            </button>
                        </motion.div>

                        {/* Helpful Links */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1 }}
                            className="mt-12"
                        >
                            <p className="text-gray-600 mb-4">Or try these helpful links:</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Link
                                    to="/universities"
                                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                                >
                                    Universities
                                </Link>
                                <Link
                                    to="/courses"
                                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                                >
                                    Courses
                                </Link>
                                <Link
                                    to="/faqs"
                                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                                >
                                    FAQs
                                </Link>
                                <Link
                                    to="/contact"
                                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                                >
                                    Contact
                                </Link>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </>
    )
}

export default NotFound
